// §S680 — THE nav registry. One source of truth for every navigation
// surface (NavRail, MobileTabBar, Sidebar drawer, CommandPalette). Before
// this file, the four surfaces each hardcoded their own destination list
// and disagreed on labels, order, and active-state rules (platform audit).
//
// Each surface picks the SUBSET it shows (rail: home…guide + help in the
// bottom cluster; tab bar: the first four; drawer + palette: all), but the
// id, hash, label key, and icon for a destination come from here — so a
// destination has exactly ONE label per locale everywhere.
import {
  Home,
  LayoutGrid,
  Sparkles,
  BookOpen,
  LifeBuoy,
  Plug,
  type LucideIcon
} from 'lucide-preact';

export type NavId = 'home' | 'workspace' | 'pomy' | 'connectors' | 'guide' | 'help';

export interface NavDestination {
  id: NavId;
  /** location.hash target, without the leading '#'. '' = home canvas. */
  hash: string;
  /** i18n catalog key — resolved with t() at render by each surface. */
  i18nKey: string;
  /** English fallback when the catalog misses (mirrors the EN value). */
  fallback: string;
  Icon: LucideIcon;
}

export const NAV_ITEMS: readonly NavDestination[] = [
  { id: 'home', hash: '', i18nKey: 'nav.home', fallback: 'Home', Icon: Home },
  { id: 'workspace', hash: 'apps', i18nKey: 'nav.workspace', fallback: 'Workspace', Icon: LayoutGrid },
  { id: 'pomy', hash: 'copilot', i18nKey: 'nav.copilot', fallback: 'Pomy', Icon: Sparkles },
  // §HUB — the Integration Hub. A catalog nobody can navigate to has the same problem as no
  // catalog at all, so it is a first-class destination rather than a hash you must already know.
  { id: 'connectors', hash: 'connectors', i18nKey: 'nav.connectors', fallback: 'Integrations', Icon: Plug },
  { id: 'guide', hash: 'guide', i18nKey: 'nav.guide', fallback: 'Guide', Icon: BookOpen },
  { id: 'help', hash: 'help', i18nKey: 'nav.help', fallback: 'Support', Icon: LifeBuoy }
];

/** Pick registry entries by id, preserving the caller's order. */
export function navItems(ids: readonly NavId[]): NavDestination[] {
  return ids
    .map((id) => NAV_ITEMS.find((item) => item.id === id))
    .filter((item): item is NavDestination => !!item);
}

/**
 * Parse the current hash route the same way CreatorRoot does — strip the
 * leading '#'/'#/' and drop any ?query suffix.
 */
export function currentRoute(): string {
  if (typeof location === 'undefined') return '';
  return (location.hash || '').replace(/^#\/?/, '').split('?')[0] ?? '';
}

/**
 * Which registry destination (if any) the given route belongs to. This is
 * the SINGLE active-state rule for every nav surface:
 *   ''                          → home
 *   apps, apps/:id, apps/:id/edit → workspace
 *   settings/account/admin/…    → null (no nav item is active)
 * Exactly one id (or none) — so at most one item ever gets aria-current.
 */
export function activeNavId(route: string = currentRoute()): NavId | null {
  if (route === '') return 'home';
  if (route === 'apps' || route.startsWith('apps/')) return 'workspace';
  if (route === 'copilot') return 'pomy';
  if (route === 'connectors') return 'connectors';
  if (route === 'guide') return 'guide';
  if (route === 'help') return 'help';
  return null;
}

/**
 * Navigate to a registry destination. The '' (home) hash needs the guard:
 * assigning location.hash = '' when it is already '' is a silent no-op,
 * and assigning it while elsewhere leaves a bare '#' — both fine, but we
 * skip the write entirely when nothing would change.
 */
export function goNav(hash: string): void {
  navigator.vibrate?.(8);
  if (hash === '') {
    if (location.hash !== '' && location.hash !== '#') location.hash = '';
    return;
  }
  location.hash = hash;
}

/** The addressable editor route for an app — the ONE way to open the editor. */
export function editorHash(appId: string): string {
  return `apps/${appId}/edit`;
}
